import type { SetToothFullHeadMPRSliceEditorStateReq } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import type { CBCT_SectionOrientation } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import {
  ToothFullHeadMPRSliceView,
  ToothFullHeadMPRSliceView_SamplingMode,
  type ToothFullHeadMPRSlice,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import type {
  FullHeadMPRFocus,
  FullHeadMPRProjection,
  FullHeadMPRSavedView,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import {
  FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION,
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  ORIENTATION_TO_SERIES,
} from '../config';

export {
  isSameFullHeadMprAnnotationPlane,
  isSameFullHeadMprPhysicalPlane,
  isSameFullHeadMprPlane,
  makeFullHeadMprPointTransform,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const resolveFullHeadMprSeriesContext = ({
  slice,
  target,
}: {
  slice?: ToothFullHeadMPRSlice;
  target?: SetToothFullHeadMPRSliceEditorStateReq['Target'];
}) => {
  const source = slice?.Source;
  if (source?.case === 'View') {
    return undefined;
  }
  const orientation =
    source?.case === 'Series'
      ? source.value.Orientation
      : slice?.Orientation ??
        (target?.case === 'Series' ? target.value.Orientation : undefined);
  const seriesName =
    orientation === undefined ? undefined : ORIENTATION_TO_SERIES[orientation];
  const projection = (
    Object.keys(FULL_HEAD_MPR_PROJECTION_TO_SERIES) as FullHeadMPRProjection[]
  ).find(
    (candidate) => FULL_HEAD_MPR_PROJECTION_TO_SERIES[candidate] === seriesName,
  );
  return orientation !== undefined && projection
    ? { orientation, projection }
    : undefined;
};

export const makeFullHeadMprViewMessage = (
  view: FullHeadMPRSavedView,
  { orientation }: { orientation?: CBCT_SectionOrientation } = {},
) => {
  const point = ({ x, y, z }: FullHeadMPRFocus) => ({ X: x, Y: y, Z: z });
  return new ToothFullHeadMPRSliceView({
    CenterMm: point(view.centerMm),
    HeightMm: view.heightMm,
    Normal: point(view.normal),
    Orientation:
      orientation ?? FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION[view.projection],
    SampleOffsetsMm: [...view.sampleOffsetsMm],
    Sampling:
      view.sampling === 'native'
        ? ToothFullHeadMPRSliceView_SamplingMode.Native
        : ToothFullHeadMPRSliceView_SamplingMode.MeanSlab,
    ThicknessMm: view.thicknessMm,
    UAxis: point(view.uAxis),
    VAxis: point(view.vAxis),
    VolumeFileID: view.volumeFileID,
    WidthMm: view.widthMm,
    WindowCenter: view.window.wc,
    WindowWidth: view.window.ww,
  });
};
